<script setup lang="ts">
import QuizAndExercises from '@/components/quiz/QuizAndExercises.vue';
import Curriculum from '@/components/videos/Curriculum.vue';
import MaterialVideoTabList from '@/components/material/MaterialVideoTabList.vue';
import RelatedArticlesList from '@/components/RelatedArticlesList.vue';
import SimilarContentList from '@/components/SimilarContentList.vue';
import Subtitles from '@/components/videos/SingleVideoSubtitles.vue';
import Keywords from '@/components/videos/SingleVideoKeywords.vue';
import { TabInfo, Tabs, Video } from '@ilteducation/videos';
import { useLocale } from '@/plugins/translation';
import config from '@/config/ConfigProvider';
import { useUserContext } from '@ilteducation/ilt-auth';
import { computed } from 'vue';
import VideosInAreaList from '@/components/videos/VideosInAreaList.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { Area, Category } from '@ilteducation/content-ref-data-types';
import { useDisplay } from 'vuetify';

type Props = {
  video: Video;
  activeTab: number;
  areaVideos?: Video[];
  tabInfo?: TabInfo;
  showHeader?: boolean;
  area: Area;
  subject: Category;
};

const props = withDefaults(defineProps<Props>(), {
  showHeader: false,
});

const emits = defineEmits<{
  (e: 'swap-tab', tab: number): void;
  (e: 'go-back'): void;
}>();

const { t, l } = useLocale();
const { mdAndUp } = useDisplay();

const currentActiveTab = computed(() => {
  return props.activeTab !== undefined ? props.activeTab : Tabs.keywords;
});

const switchTab = (tab: Tabs) => {
  emits('swap-tab', tab);
};

const feedbackFormUrl = config.value('feedbackFormUrl');
const userContext = useUserContext();
const userIsNotStudent = computed(() => !userContext.hasRole('student'));
</script>

<template>
  <v-window v-model="currentActiveTab" class="film-tabs-window">
    <div v-if="showHeader" class="film-tab-header d-flex position-sticky top-0 pt-4 bg-surface">
      <v-btn size="48" variant="text" @click="emits('go-back')">
        <FontAwesomeIcon icon="fa-regular fa-chevron-left" />
      </v-btn>
      <h2 class="subheader film-tab-content-header">{{ tabInfo?.title }}</h2>
    </div>
    <v-window-item v-if="areaVideos" :value="Tabs.videosInArea">
      <v-container class="custom-container" :class="{ 'px-16': mdAndUp }">
        <VideosInAreaList
          :video="video"
          :area-videos="areaVideos"
          :show-header="false"
          :area="area"
          :subject="subject"
          @click-video="emits('go-back')"
        />
      </v-container>
    </v-window-item>
    <v-window-item :value="Tabs.keywords">
      <v-container class="custom-container" :class="{ 'px-16': mdAndUp }">
        <Keywords :video="video" />
      </v-container>
    </v-window-item>
    <v-window-item :value="Tabs.quiz">
      <v-container class="custom-container" :class="{ 'px-16': mdAndUp }">
        <QuizAndExercises :video="video" @swap-tab="switchTab(Tabs.immerse)" />
      </v-container>
    </v-window-item>
    <v-window-item :value="Tabs.subtitles">
      <v-container class="custom-container" :class="{ 'px-16': mdAndUp }">
        <Subtitles :video="video" />
      </v-container>
    </v-window-item>
    <v-window-item :eager="true" :value="Tabs.materials">
      <v-container class="custom-container" :class="{ 'px-16': mdAndUp }">
        <MaterialVideoTabList :video-id="video.id" :category-id="props.subject.id" :area-id="props.area.id" />
      </v-container>
    </v-window-item>
    <v-window-item :eager="true" :value="Tabs.articles">
      <v-container class="custom-container" :class="{ 'px-16': mdAndUp }">
        <v-row class="mx-0">
          <v-col cols="12">
            <RelatedArticlesList v-if="video" :key="video.id" :context-key="video.id" :content-ids="[video.id]" />
          </v-col>
        </v-row>
      </v-container>
    </v-window-item>
    <v-window-item :eager="true" :value="Tabs.immerse">
      <v-container class="custom-container" :class="{ 'px-16': mdAndUp }">
        <v-row class="mx-0">
          <v-col cols="12">
            <div class="related-books-message bg-surface">
              <p>
                {{ userIsNotStudent ? t(l.film.textbooks_explainer_teacher) : t(l.film.textbooks_explainer_student) }}
              </p>
              <a v-if="userIsNotStudent" target="_blank" :href="feedbackFormUrl">{{ t(l.menu.feedback_form) }}</a>
            </div>
            <SimilarContentList v-if="video" :key="video.id" :context-key="video.id" :content-ids="[video.id]" />
          </v-col>
        </v-row>
      </v-container>
    </v-window-item>
    <v-window-item :value="Tabs.curriculum">
      <v-container class="custom-container" :class="{ 'px-16': mdAndUp }">
        <v-row class="mx-0">
          <v-col>
            <Curriculum />
          </v-col>
        </v-row>
      </v-container>
    </v-window-item>
  </v-window>
</template>

<style scoped lang="scss">
.custom-container {
  max-width: 93rem !important;
  /* Override nested v-container max-widths */
  .v-container {
    max-width: 100% !important;
    width: 100%;
    padding-left: 0;
    padding-right: 0;
  }
}

.film-tabs-window {
  // overflow: hidden would stop the sticky header from sticking
  overflow: clip;
}

.film-tab-header {
  z-index: 1;
}

.film-tab-content-header {
  font-size: 1.5rem;
}

.related-books-message {
  background: rgb(var(--v-theme-surface));
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 0.875rem;
  line-height: 1.6;
  overflow: hidden;
  padding-inline: 0;
  padding-top: 16px;
  padding-bottom: 16px;
  position: relative;
  width: 100%;

  box-shadow:
    0 0 0 0 var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)),
    0 0 0 0 var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.14)),
    0 0 0 0 var(--v-shadow-key-ambient-opacity, rgba(0, 0, 0, 0.12));
  border-color: rgba(var(--v-border-color), var(--v-border-opacity));
  border-style: solid;
  border-width: 0 0 thin 0;
}
</style>
